import React, { memo, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Grid, List } from "react-window";
import { Link } from "react-router";
import styles from "./PlanningWeek.module.scss";
import { shownWorkers } from "./planningData";
import WorkerLabel from "./WorkerLabel";

const ROW_HEIGHT = 48;
const HEADER_ROW_HEIGHTS = [44, 44, 32];
const MIN_DAY_WIDTH = 80;

/**
 * Day header, events without site, total hours.
 */
const HeaderCell = ({columnIndex, rowIndex, style, days, index, openDay}) => {
  const day = days[columnIndex];

  if (rowIndex === 0) {
    return <div style={style} className={clsx(styles.cell, styles.dayHeader, {
      [styles.today]: day.isToday,
      [styles.we]: day.isWeekend
    })}>
      <span className={styles.dayHeaderName}>{day.weekday}</span>
      <span className={styles.dayHeaderNumber}>{Number(day.dayOfMonth)}</span>
    </div>;
  }

  if (rowIndex === 1) {
    const customEvents = index.customEvents.get(day.key) || [];

    return <div style={style}
                className={clsx(styles.cell, styles.openable, styles.customEventsCell, day.isWeekend && styles.we)}
                onClick={() => openDay(day.key, null)}>
      {customEvents.map(event => <div key={event.id} className={styles.cellEvent}>{event.name}</div>)}
    </div>;
  }

  const hours = index.totalHours.get(day.key);

  return <div style={style} className={clsx(styles.cell, styles.totalCell, day.isWeekend && styles.we)}>
    {hours > 0 ? hours + "h" : null}
  </div>;
};

/**
 * A site's day: the events, then the workers with hours or an alert ("Jean 8h · Marie 3.5h").
 */
const SiteDayCell = ({columnIndex, rowIndex, style, days, sites, index, workersById, openDay}) => {
  const day = days[columnIndex];
  const site = sites[rowIndex];

  const siteDay = index.siteDays.get(site.id + "|" + day.key);
  const isActive = site.dateStart <= day.key && day.key <= site.activeEnd;

  const workers = shownWorkers(siteDay?.workers || new Map(), workersById);

  return <div style={style}
              className={clsx(styles.cell, styles.openable, {
                [styles.inactive]: !isActive,
                [styles.we]: day.isWeekend,
                [styles.todayColumn]: day.isToday
              })}
              onClick={() => openDay(day.key, site)}>
    {siteDay?.events.map(event => <div key={event.id} className={styles.cellEvent}>{event.name}</div>)}
    {workers.length > 0 && <div className={clsx(styles.cellWorkers, siteDay.events.length > 0 && styles.oneLine)}>
      {workers.map((worker, i) => <React.Fragment key={worker.workerId}>
        {i > 0 && " · "}
        <WorkerLabel worker={worker}/>
      </React.Fragment>)}
    </div>}
  </div>;
};

const SiteNameRow = ({index, style, sites}) => {
  const site = sites[index];
  const siteColor = site.color.split("|");

  return <div style={style} className={styles.siteName}>
    <Link to={"/sites/" + site.id}
          className={styles.siteNameLink}
          style={{backgroundColor: siteColor[0], color: siteColor[1]}}>
      {site.name}
    </Link>
  </div>;
};

/**
 * Several days side by side (3 on phones, 7 wider), scrolling day by day across the whole period.
 * The day header sticks to the top and the site names to the left; only the visible cells are rendered.
 * scrollTarget ({key, smooth}) scrolls to a day; the first visible day is reported back.
 */
const PlanningMultiDay = memo(({
  days,
  sites,
  index,
  workersById,
  siteColumnWidth,
  nbVisibleDays,
  snap,
  scrollTarget,
  onFirstVisibleDay,
  openDay
}) => {
  const [scroller, setScroller] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const firstVisibleIndex = useRef(0);
  const snapTimer = useRef(null);
  // Day reached by a scroll of our own: the days passed on the way are not reported
  const scrollingTo = useRef(null);
  const ownScrollId = useRef(0);

  const dayWidth = viewportWidth > 0
    ? Math.max(MIN_DAY_WIDTH, Math.floor((viewportWidth - siteColumnWidth) / nbVisibleDays))
    : MIN_DAY_WIDTH * 2;

  const headerHeight = HEADER_ROW_HEIGHTS.reduce((acc, height) => acc + height, 0);

  useLayoutEffect(() => {
    if (!scroller) {
      return undefined;
    }

    // Measured right away (the observer may not report the first size), then on every resize
    const measure = () => setViewportWidth(scroller.clientWidth);
    const observer = new ResizeObserver(measure);

    measure();
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [scroller]);

  const endOwnScroll = () => {
    clearTimeout(snapTimer.current);
    scrollingTo.current = null;

    // Snapping comes back once the cells at the destination are rendered (two frames), unless a new scroll started
    const scrollId = ++ownScrollId.current;

    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (scroller && scrollId === ownScrollId.current && scrollingTo.current === null) {
        scroller.style.scrollSnapType = "";
      }
    }));
  };

  // Snapping would stop a jump at the last rendered day: it is paused until the destination is reached.
  // The days passed on the way are not reported, so that the week strip goes straight to the destination.
  const scrollToIndex = (dayIndex, smooth) => {
    if (!scroller) {
      return;
    }

    const left = dayIndex * dayWidth;

    if (Math.abs(scroller.scrollLeft - left) < 1) {
      endOwnScroll();
      return;
    }

    clearTimeout(snapTimer.current);
    ownScrollId.current++;
    scroller.style.scrollSnapType = "none";
    scrollingTo.current = dayIndex;
    scroller.scrollTo({left, behavior: smooth ? "smooth" : "auto"});
    // Last resort if the destination is never reached exactly
    snapTimer.current = setTimeout(endOwnScroll, 3000);
  };

  // Requested day (initial day, "Aujourd'hui", a day of the week strip...)
  useLayoutEffect(() => {
    const targetIndex = days.findIndex(day => day.key === scrollTarget.key);

    if (scroller && viewportWidth > 0 && targetIndex >= 0) {
      firstVisibleIndex.current = targetIndex;
      onFirstVisibleDay(days[targetIndex].key);
      scrollToIndex(targetIndex, scrollTarget.smooth);
    }
    // Not run again when dayWidth changes: a new width keeps the first visible day (below), not the requested one
  }, [scroller, scrollTarget, days, viewportWidth > 0]);

  // When the days get wider or narrower (rotation, resize), the first visible day stays the same
  useLayoutEffect(() => {
    scrollToIndex(firstVisibleIndex.current, false);
  }, [scroller, dayWidth]);

  const onScroll = event => {
    const first = Math.min(Math.round(event.currentTarget.scrollLeft / dayWidth), days.length - 1);

    if (scrollingTo.current !== null) {
      if (Math.abs(event.currentTarget.scrollLeft - scrollingTo.current * dayWidth) >= 1) {
        return;
      }

      endOwnScroll();
    }

    if (first !== firstVisibleIndex.current) {
      firstVisibleIndex.current = first;
      onFirstVisibleDay(days[first].key);
    }
  };

  return <div className={clsx(styles.multiDay, snap && styles.snap)}
              ref={setScroller}
              onScroll={onScroll}
              // The user takes over the scrolling
              onWheel={endOwnScroll}
              onTouchStart={endOwnScroll}
              onPointerDown={endOwnScroll}
              style={{scrollPaddingLeft: siteColumnWidth}}>
    <div className={styles.multiDayContent}
         style={{
           gridTemplateColumns: siteColumnWidth + "px " + (days.length * dayWidth) + "px",
           gridTemplateRows: headerHeight + "px " + (sites.length * ROW_HEIGHT) + "px"
         }}>
      <div className={styles.corner}>
        <div style={{height: HEADER_ROW_HEIGHTS[0]}}/>
        <div style={{height: HEADER_ROW_HEIGHTS[1]}}/>
        <div className={styles.cornerTotal} style={{height: HEADER_ROW_HEIGHTS[2]}}>Total</div>
      </div>

      <div className={styles.header}>
        <Grid cellComponent={HeaderCell}
              cellProps={{days, index, openDay}}
              columnCount={days.length}
              columnWidth={dayWidth}
              rowCount={HEADER_ROW_HEIGHTS.length}
              rowHeight={rowIndex => HEADER_ROW_HEIGHTS[rowIndex]}
              scrollElement={scroller}
              // Not a scroll container of its own, so that its day cells are snap points of the scrolling element
              style={{width: "max-content", overflow: "visible"}}/>
      </div>

      <div className={styles.siteNames}>
        <List rowComponent={SiteNameRow}
              rowProps={{sites}}
              rowCount={sites.length}
              rowHeight={ROW_HEIGHT}
              scrollElement={scroller}/>
      </div>

      <Grid cellComponent={SiteDayCell}
            cellProps={{days, sites, index, workersById, openDay}}
            columnCount={days.length}
            columnWidth={dayWidth}
            rowCount={sites.length}
            rowHeight={ROW_HEIGHT}
            overscanCount={2}
            scrollElement={scroller}
            style={{width: "max-content"}}/>
    </div>
  </div>;
});

export default PlanningMultiDay;
